// Mouvement de l'accroche d'une vidéo (« C'est quoi, Pythagore ??? ») :
// les mots claquent un à un, les points d'interrogation rebondissent, l'image
// tremble quand la question est posée. Pur : une image = une fonction du
// temps de la scène (pause, reprise, capture vidéo et mouvement réduit).

/** Un morceau de l'accroche : un mot, ou sa ponctuation finale (« ??? », « ! »). */
export interface HookToken {
  text: string;
  /** mot à faire ressortir (`bookends.hook.emphasis`) */
  emphasis: boolean;
  /** ponctuation finale : chaque signe rebondit à part */
  marks: boolean;
  /** collé au morceau précédent (« Pythagore??? ») */
  glued: boolean;
}

const MARKS_RE = /[?!‽]+$/u;
const SPACE_RE = /\s+/u; // espaces, insécables comprises

/** Découpe l'accroche en mots et ponctuations finales ; repère le mot mis en valeur. */
export function hookTokens(text: string, emphasis?: string | null): HookToken[] {
  const lower = text.toLowerCase();
  const at = emphasis ? lower.indexOf(emphasis.trim().toLowerCase()) : -1;
  const range: readonly [number, number] | null =
    at >= 0 && emphasis ? [at, at + emphasis.trim().length] : null;
  const out: HookToken[] = [];
  let pos = 0;
  for (const raw of text.split(SPACE_RE)) {
    if (!raw) continue;
    const start = text.indexOf(raw, pos);
    pos = start + raw.length;
    const m = MARKS_RE.exec(raw);
    const word = m && m.index > 0 ? raw.slice(0, m.index) : m ? '' : raw;
    if (word) {
      const end = start + word.length;
      out.push({
        text: word,
        emphasis: !!range && start < range[1] && end > range[0],
        marks: false,
        glued: false,
      });
    }
    if (m) out.push({ text: m[0], emphasis: false, marks: true, glued: !!word });
  }
  return out;
}

/** Unité collée au nombre qui la précède (« +20 % », « 33 € », « 7 °C ») : jamais seule à la ligne. */
const UNIT_RE = /^[%€°‰]/u;
const ENDS_WITH_DIGIT_RE = /\d$/u;

/**
 * Morceaux qui restent sur la même ligne : un signe seul (« ? », « ! ») reste
 * collé à son mot ; « ??? » peut claquer seul sur sa ligne ; une unité reste
 * avec son nombre.
 */
export function hookGroups(tokens: readonly HookToken[]): number[][] {
  const groups: number[][] = [];
  tokens.forEach((tok, i) => {
    const prev = tokens[i - 1];
    const unit =
      !tok.marks && UNIT_RE.test(tok.text) && !!prev && ENDS_WITH_DIGIT_RE.test(prev.text);
    const stick =
      groups.length > 0 && ((tok.marks && (tok.glued || [...tok.text].length === 1)) || unit);
    if (stick) groups[groups.length - 1]!.push(i);
    else groups.push([i]);
  });
  return groups;
}

/** Durée du claquement d'un mot (ms). */
export const HOOK_SLAM_MS = 340;
/** Décalage entre deux signes d'une ponctuation qui rebondit (ms). */
export const HOOK_MARK_STAGGER_MS = 90;
/** Durée de la secousse quand la question est posée (ms). */
export const HOOK_SHAKE_MS = 420;

/** Débuts des morceaux (ms de scène) et fin de la pose de la question. */
export function hookSchedule(tokens: readonly HookToken[]): {
  starts: number[];
  landedMs: number;
} {
  const n = Math.max(1, tokens.length);
  const step = Math.min(260, Math.max(150, 1000 / n));
  const starts = tokens.map((_, i) => Math.round(150 + i * step));
  const last = tokens.length - 1;
  const lastLen = last >= 0 && tokens[last]!.marks ? tokens[last]!.text.length : 1;
  const landedMs =
    last < 0 ? 0 : starts[last]! + HOOK_SLAM_MS + (lastLen - 1) * HOOK_MARK_STAGGER_MS;
  return { starts, landedMs };
}

const clamp01 = (x: number) => Math.min(1, Math.max(0, x));
const r3 = (x: number) => Math.round(x * 1000) / 1000;
const r1 = (x: number) => Math.round(x * 10) / 10;

function outBack(p: number): number {
  const c1 = 2.2;
  const c3 = c1 + 1;
  return 1 + c3 * (p - 1) ** 3 + c1 * (p - 1) ** 2;
}

function outBounce(p: number): number {
  const n = 7.5625;
  const d = 2.75;
  if (p < 1 / d) return n * p * p;
  if (p < 2 / d) return n * (p -= 1.5 / d) * p + 0.75;
  if (p < 2.5 / d) return n * (p -= 2.25 / d) * p + 0.9375;
  return n * (p -= 2.625 / d) * p + 0.984375;
}

export interface HookPartStyle {
  opacity: number;
  transform: string;
}

export interface HookFrame {
  /** un style par morceau (les ponctuations : celui de leur bloc) */
  tokens: HookPartStyle[];
  /** un style par signe des ponctuations, dans l'ordre des morceaux */
  marks: HookPartStyle[][];
  /** secousse de tout le texte */
  shake: string;
  /** éclair blanc quand le premier mot claque (opacité) */
  flash: number;
  /** rayons derrière le texte */
  rays: HookPartStyle;
  /** grand « ? » en fond */
  bigMark: HookPartStyle;
}

/** Image de l'accroche à l'instant `t` (ms de scène) ; `reduced` : image finale, immobile. */
export function hookFrame(tokens: readonly HookToken[], t: number, reduced = false): HookFrame {
  const { starts, landedMs } = hookSchedule(tokens);
  const time = reduced ? Number.POSITIVE_INFINITY : t;
  const tokenStyles: HookPartStyle[] = [];
  const marks: HookPartStyle[][] = [];
  tokens.forEach((tok, i) => {
    const local = time - starts[i]!;
    if (tok.marks) {
      tokenStyles.push({ opacity: local >= 0 ? 1 : 0, transform: 'none' });
      marks.push(
        [...tok.text].map((_, j) => {
          const lt = local - j * HOOK_MARK_STAGGER_MS;
          const p = clamp01(lt / (HOOK_SLAM_MS + 120));
          if (reduced) return { opacity: 1, transform: 'none' };
          const fall = (1 - outBounce(p)) * -46;
          // posés, ils se dandinent (le temps de la scène : figés en pause)
          const wiggle = p >= 1 ? 7 * Math.sin((lt - HOOK_SLAM_MS) / 170 + j * 1.3) : 0;
          const tilt = (1 - p) * (j % 2 ? -28 : 24) + wiggle;
          return {
            opacity: r3(clamp01(lt / 90)),
            transform: `translateY(${r1(fall)}px) rotate(${r1(tilt)}deg)`,
          };
        }),
      );
      return;
    }
    const p = clamp01(local / HOOK_SLAM_MS);
    if (reduced || p >= 1) {
      tokenStyles.push({ opacity: 1, transform: 'none' });
      return;
    }
    // arrive de très près, s'écrase un peu, se pose
    const scale = 1 + 1.5 * (1 - outBack(p));
    const tilt = (1 - p) * (i % 2 ? 7 : -6);
    tokenStyles.push({
      opacity: r3(clamp01(p * 3.5)),
      transform: `scale(${r3(scale)}) rotate(${r1(tilt)}deg)`,
    });
  });

  // la question posée : secousse, puis on s'approche doucement
  let shake = 'none';
  if (!reduced) {
    const sp = (time - (landedMs - 60)) / HOOK_SHAKE_MS;
    const a = sp >= 0 && sp < 1 ? Math.exp(-4.5 * sp) : 0;
    const dx = a * 9 * Math.sin(2 * Math.PI * 5 * sp);
    const dy = a * 4 * Math.sin(2 * Math.PI * 7 * sp + 1);
    const push = 1 + 0.06 * clamp01((time - landedMs) / 1600);
    if (a > 0 || push > 1) shake = `translate(${r1(dx)}px, ${r1(dy)}px) scale(${r3(push)})`;
  }
  // éclair bref quand le premier mot touche
  const flashAt = (starts[0] ?? 0) + HOOK_SLAM_MS * 0.55;
  const flash = reduced || time < flashAt ? 0 : r3(0.34 * (1 - clamp01((time - flashAt) / 170)));
  const raysIn = clamp01((time - (starts[0] ?? 0)) / 450);
  const rays = {
    opacity: r3(raysIn),
    transform: `translate(-50%, -50%) rotate(${reduced ? 0 : r1((t * 0.012) % 360)}deg)`,
  };
  // grand « ? » penché dans le coin : tombe, se pose, se balance
  const qIn = clamp01(time / 650);
  const qEase = 1 - (1 - qIn) ** 3;
  const bigMark = {
    opacity: r3(clamp01(time / 350)),
    transform: reduced
      ? 'translate(-50%, -50%) rotate(16deg)'
      : `translate(-50%, -50%) scale(${r3(1.6 - 0.6 * qEase)}) rotate(${r1(
          40 - 24 * qEase + 3 * Math.sin(t / 420),
        )}deg)`,
  };
  return { tokens: tokenStyles, marks, shake, flash, rays, bigMark };
}

/**
 * Taille du texte : aussi gros que possible dans 310 px de large — le mot le
 * plus long tient sur une ligne, la phrase dans environ 270 px de haut. Le
 * calque la réduit encore si un mot déborde une fois la police chargée.
 */
export function hookFontSize(text: string): number {
  const tokens = hookTokens(text);
  const units = hookGroups(tokens).map((g) =>
    g.reduce((n, i, k) => n + [...tokens[i]!.text].length + (k > 0 ? 0.3 : 0), 0),
  );
  const longest = Math.max(1, ...units);
  const total = Math.max(1, [...text].length);
  // Outfit extra-gras : ~0,54 em par lettre en minuscules (mesuré), marge comprise
  const byWord = 310 / (0.58 * longest);
  const byArea = Math.sqrt((310 * 270) / (0.58 * 1.08 * total));
  return Math.floor(Math.max(28, Math.min(66, byWord, byArea)));
}
